// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Button from "@store/MobiusOverrides/Button/Button";
import { ButtonPresentationProps, ButtonProps } from "@store/MobiusOverrides/Button/Props";
import { transitionControlStyles } from "@store/Themes/common/mixins/transition";
import { buttonResetProps } from "@store/Themes/common/props/button";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues, { BreakpointValueProp } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { DataOption, DataOptions, getDataOptionValue } from "@toolkit/Framework/Common/Utilities/dataOptions";
import getCleanBreakpointValues from "@toolkit/Framework/Common/Utilities/getCleanBreakpointValues";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import overrideProps from "@toolkit/Framework/Common/Utilities/overrideProps";
import { pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, HTMLAttributes, useMemo } from "react";
import { css } from "styled-components";

export type CarouselDotsVariantOption = DataOption<Omit<CarouselDotsPresentationProps, "variant">>;
export interface CarouselDotsVariantOptionMapper {
    standard: CarouselDotsVariantOption;
    outline: CarouselDotsVariantOption;
}
export type CarouselDotsVariantOptions = DataOptions<CarouselDotsVariantOption> & CarouselDotsVariantOptionMapper;
export type CarouselDotsVariant = keyof CarouselDotsVariantOptionMapper;

export const carouselDotsVariantOptions: CarouselDotsVariantOptions = {
    standard: {
        id: "standard",
        displayName: "Standard",
        isDefault: true,
        value: {
            color: "common.disabledDark",
            selectedColor: "primary",
            extendedStyles: {
                button: {
                    css: css`
                        background-color: currentColor;

                        .carousel.invert-color && {
                            color: ${getColor("invert.dark")};

                            &[aria-current="true"] {
                                color: ${getColor("invert")};
                            }
                        }
                    `,
                },
            },
        },
    },
    outline: {
        id: "outline",
        displayName: "Outline",
        value: {
            color: "primary",
            selectedColor: "primary",
            extendedStyles: {
                button: {
                    css: css`
                        border: 2px solid currentColor;

                        &[aria-current="true"] {
                            background-color: currentColor;
                        }

                        .carousel.invert-color && {
                            color: ${getColor("invert")};
                        }
                    `,
                },
            },
        },
    },
};

export interface CarouselDotProps {
    button?: ButtonProps;
    selected?: boolean;
}

export interface CarouselDotsPresentationProps extends ExpandStyles<CarouselDotsStyles> {
    color?: string;
    gap?: BreakpointValueProp;
    selectedColor?: string;
    size?: BreakpointValueProp;
    variant?: CarouselDotsVariant;
}

export interface CarouselDotsProps extends CarouselDotsPresentationProps, HTMLAttributes<HTMLDivElement> {
    dots: CarouselDotProps[] | undefined;
}

export interface CarouselDotsStyles {
    wrapper?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const carouselDotsStyles: CarouselDotsStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
        `,
    },
    button: {
        ...buttonResetProps,
        css: css`
            ${buttonResetProps.css}
            border-radius: 50%;
            transition: ${transitionControlStyles("background-color", "border-color", "color")};
            pointer-events: all;

            &[aria-current="true"] {
                cursor: default;
            }
        `,
    },
};

const CarouselDots: FC<CarouselDotsProps> = ({
    children,
    className,
    color: baseColor,
    dots,
    extendedStyles: baseExtendedStyles,
    gap: baseGap,
    selectedColor: baseSelectedColor,
    size: baseSize = pxToRemArray([8]),
    overridedStyles: baseOverridedStyles,
    variant = "outline",
    ...htmlAttributes
}) => {
    const variantData = useMemo(() => getDataOptionValue(carouselDotsVariantOptions, variant), [variant]);
    const color = baseColor || variantData?.color;

    const extendedStyles = useMemo(
        () => mergeProps(variantData?.extendedStyles, baseExtendedStyles),
        [baseExtendedStyles, variantData?.extendedStyles],
    );

    const gap = useMemo(() => getCleanBreakpointValues(baseGap || variantData?.gap), [baseGap, variantData?.gap]);
    const size = useMemo(() => getCleanBreakpointValues(baseSize || variantData?.size), [baseSize, variantData?.size]);
    const selectedColor = baseSelectedColor || variantData?.selectedColor;

    const overridedStyles = useMemo(
        () => overrideProps(variantData?.overridedStyles, baseOverridedStyles),
        [baseOverridedStyles, variantData?.overridedStyles],
    );

    const expandedStyles = useExpandProps(carouselDotsStyles, extendedStyles, overridedStyles);

    const styles = useMemo(() => {
        return mergeProps<CarouselDotsStyles>(expandedStyles, {
            wrapper: {
                css: css`
                    ${breakpointValues(gap || size?.map(x => (x !== null ? `calc(0.75 * ${x}) ${x}` : null)), "gap")}
                `,
            },
            button: {
                css: css`
                    ${breakpointValues(size, ["width", "height"])}

                    ${() => {
                        if (color) {
                            return css`
                                color: ${getColor(color)};
                            `;
                        }
                    }}

                    ${() => {
                        if (selectedColor) {
                            return css`
                                &[aria-current="true"] {
                                    color: ${getColor(selectedColor)};
                                }
                            `;
                        }
                    }}
                `,
            },
        });
    }, [color, expandedStyles, gap, selectedColor, size]);

    if (!dots?.length) {
        return null;
    }

    return (
        <StyledWrapper
            className={`carousel-dot-wrapper${className ? ` ${className}` : ""}`}
            {...styles.wrapper}
            {...htmlAttributes}
        >
            {dots.map(({ selected, button }, index) => {
                return (
                    <Button
                        key={index}
                        aria-current={selected}
                        {...styles.button}
                        {...button}
                        className={`carousel-dot${button?.className ? ` ${button.className}` : ""}`}
                    >
                        <VisuallyHidden>
                            {translate("Go to slide {0} of {1}", `${index + 1}`, `${dots.length}`)}
                        </VisuallyHidden>
                    </Button>
                );
            })}
        </StyledWrapper>
    );
};

export default CarouselDots;
